import React, { FC } from 'react'
import Chart from 'react-apexcharts';
import { add, startOfMonth, parseISO } from 'date-fns'
import { getCSSVariableValue } from '@/_metronic/assets/ts/_utils'
import { IDatePad, ITrafficDataPoint } from '../../../api';

type Props = {
  from: Date;
  to: Date;
  height: number;
  pad: IDatePad;
  data: ITrafficDataPoint[];
}

const fill = (from: Date, to: Date, data: ITrafficDataPoint[], unit: IDatePad): ITrafficDataPoint[] => {
  const ticksMap = new Map<number, ITrafficDataPoint>();
  const unitKey = unit + 's';
  let current;

  switch (unit) {
    case 'day':
    case 'hour':
      current = from;
      break;
    default:
      current = startOfMonth(from);
  }

  while (current.getTime() < to.getTime()) {
    ticksMap.set(current.getTime(), {
      visitors: 0,
      pageViews: 0,
      period: current
    });
    current = add(current, { [unitKey]: 1 });
  }

  // Remplir avec les vraies données
  data.forEach(entry => {
    // Parser la date ISO (avec timezone) correctement
    const periodDate = typeof entry.period === 'string'
      ? parseISO(entry.period)
      : new Date(entry.period);

    const timestamp = periodDate.getTime();

    if (ticksMap.has(timestamp)) {
      ticksMap.set(timestamp, {
        visitors: entry.visitors,
        pageViews: entry.pageViews,
        period: periodDate
      });
    }
  });

  return Array.from(ticksMap.values());
};

const EvolutionChart: FC<Props> = ({ from, to, height, pad, data }) => {
  const filledData = fill(from, to, data, pad);

  const series = [
    { name: 'Pages view', type: 'column', data: filledData.map(d => d.pageViews) },
    { name: 'Visitors', type: 'line', data: filledData.map(d => d.visitors) }
  ]

  const options: ApexCharts.ApexOptions = {
    chart: {
      id: 'analytics',
      type: "line",
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false }
    },
    colors: [
      getCSSVariableValue('--bs-primary'),
      getCSSVariableValue('--bs-warning'),
      getCSSVariableValue('--bs-gray-600'),
    ],
    stroke: {
      width: [0, 4]
    },
    xaxis: {
      type: 'datetime' as const,
      categories: filledData.map(d => d.period.getTime()),
      min: filledData.at(0)?.period?.getTime(),
      max: filledData.at(-1)?.period?.getTime(),
      labels: {
        datetimeUTC: false,
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM yyyy',
          day: 'dd MMM yyyy',
          hour: 'HH:mm'
        }
      }
    },
    tooltip: {
      x: {
        format: pad === 'hour' ? 'dd MMM yyyy - HH:mm' : 'dd MMM yyyy'
      }
    },
    dataLabels: { enabled: false },
    markers: { size: 5 },
  };

  return (
    <Chart options={options} series={series} type="line" height={height} />
  )
}

export default EvolutionChart
